import * as React from 'react'
import { Text as RNText, StyleSheet, TextStyle } from 'react-native'
import { colors } from './theme'
import { useBreakpoints, ResponsiveValue } from './hooks/useBreakpoints'

type TextAlign = 'left' | 'center' | 'right' | 'justify'
type TextLevel = 'headline' | 'subheadline' | 'large' | 'body' | 'small' | 'xsmall'
type TextWeight = 'regular' | 'medium' | 'semibold' | 'strong'
type TextTone = 'neutral' | 'secondary' | 'tertiary' | 'critical' | 'positive'

interface TextProps extends Omit<React.ComponentProps<typeof RNText>, 'style'> {
	children: React.ReactNode
	level?: TextLevel
	weight?: TextWeight
	tone?: TextTone
	align?: TextAlign | ResponsiveValue<TextAlign>
	style?: TextStyle | TextStyle[]
}

const fontSizes = {
	headline: 24,
	subheadline: 20,
	large: 18,
	body: 16,
	small: 14,
	xsmall: 12,
} as const

const fontWeights = {
	regular: '400',
	medium: '500',
	semibold: '600',
	strong: '700',
} as const

const toneColors = {
	neutral: colors.gray900,
	secondary: colors.gray500,
	tertiary: colors.gray600,
	critical: colors.red500,
	positive: colors.green500,
} as const

export function Text({
	children,
	level = 'body',
	weight = 'regular',
	tone = 'neutral',
	align,
	style,
	...props
}: TextProps) {
	const { value } = useBreakpoints()
	const textAlign =
		typeof align === 'object'
			? value({
					mobile: align.mobile || 'left',
					tablet: align.tablet,
					desktop: align.desktop,
				})
			: align

	return (
		<RNText
			style={[
				styles[level],
				styles[weight],
				{
					color: toneColors[tone],
					textAlign,
				},
				style,
			]}
			{...props}
		>
			{children}
		</RNText>
	)
}

const styles = StyleSheet.create({
	base: {
		margin: 0,
		width: '100%',
	},
	headline: {
		fontSize: fontSizes.headline,
	},
	subheadline: {
		fontSize: fontSizes.subheadline,
	},
	body: {
		fontSize: fontSizes.body,
	},
	large: {
		fontSize: fontSizes.large,
	},
	small: {
		fontSize: fontSizes.small,
	},
	xsmall: {
		fontSize: fontSizes.xsmall,
	},
	regular: {
		fontWeight: fontWeights.regular,
	},
	medium: {
		fontWeight: fontWeights.medium,
	},
	semibold: {
		fontWeight: fontWeights.semibold,
	},
	strong: {
		fontWeight: fontWeights.strong,
	},
})
